import { defineStore } from "pinia";
import { ref } from "vue";

export type ToastAction = { label: string; handler: () => void };

export type Toast = {
  id: number;
  kind: "info" | "success" | "warn" | "error";
  message: string;
  action?: ToastAction;
  /** ms until auto-dismiss; null = sticky (user must close). default 5000 */
  duration?: number | null;
};

const DEFAULT_DURATION = 5000;

export const useUiStore = defineStore("ui", () => {
  const sidePanelLeadId = ref<number | null>(null);
  const toasts = ref<Toast[]>([]);
  const sidebarOpenMobile = ref(false);
  let nextToastId = 1;
  const timers = new Map<number, ReturnType<typeof setTimeout>>();

  function openSidePanel(leadId: number): void {
    sidePanelLeadId.value = leadId;
  }

  function closeSidePanel(): void {
    sidePanelLeadId.value = null;
  }

  /** Klik w kartę: ten sam lead → zamknij (toggle), inny → przełącz na niego. */
  function toggleSidePanel(leadId: number): void {
    sidePanelLeadId.value = sidePanelLeadId.value === leadId ? null : leadId;
  }

  function toggleSidebar(): void {
    sidebarOpenMobile.value = !sidebarOpenMobile.value;
  }

  function closeSidebar(): void {
    sidebarOpenMobile.value = false;
  }

  function dismissToast(id: number): void {
    const t = timers.get(id);
    if (t) {
      clearTimeout(t);
      timers.delete(id);
    }
    toasts.value = toasts.value.filter((tt) => tt.id !== id);
  }

  /**
   * Push a toast. Action handler is wrapped to auto-dismiss the toast
   * when clicked — typical "Cofnij" pattern from UX-findings §3 (17).
   */
  function pushToast(
    kind: Toast["kind"],
    message: string,
    action?: ToastAction,
    duration: number | null = DEFAULT_DURATION,
  ): number {
    const id = nextToastId++;
    const wrappedAction: ToastAction | undefined = action
      ? {
          label: action.label,
          handler: () => {
            action.handler();
            dismissToast(id);
          },
        }
      : undefined;
    toasts.value.push({ id, kind, message, action: wrappedAction, duration });
    if (duration !== null) {
      timers.set(
        id,
        setTimeout(() => dismissToast(id), duration),
      );
    }
    return id;
  }

  return {
    sidePanelLeadId,
    toasts,
    sidebarOpenMobile,
    openSidePanel,
    closeSidePanel,
    toggleSidePanel,
    toggleSidebar,
    closeSidebar,
    pushToast,
    dismissToast,
  };
});
